import { Link } from 'react-router-dom';

const PRODUCTS = [
  {
    name: 'AIBOX Edge Appliance',
    tagline: 'On-Premise AI Appliance',
    description:
      'An on-premise AI appliance designed for secure data processing, real-time analytics, and intelligent decision support for enterprise operations.',
    features: [
      'On-Premise AI',
      'Secure Processing',
      'Real-Time Analytics',
      'Edge Computing',
    ],
    link: '/aibox',
    color: 'cyan',
    icon: (
      <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 002.25-2.25V6.75a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 6.75v10.5a2.25 2.25 0 002.25 2.25zm.75-12h9v9h-9v-9z" />
      </svg>
    ),
  },
  {
    name: 'Cyber AI Assistant',
    tagline: 'Cybersecurity Assessment',
    description:
      'An AI-powered platform for cybersecurity assessment, security analysis, and secure network operations.',
    features: [
      'Security Analysis',
      'Security Assessment',
      'AI-Assisted',
      'Compliance Ready',
    ],
    link: '/contact',
    color: 'blue',
    icon: (
      <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
      </svg>
    ),
  },
  {
    name: 'Analytics Platform',
    tagline: 'Data Fusion & Analysis',
    description:
      'A comprehensive AI platform for data fusion, pattern recognition, and automated report generation.',
    features: [
      'Data Fusion',
      'Pattern Recognition',
      'Secure Reports',
      'Multi-Source',
    ],
    link: '/contact',
    color: 'purple',
    icon: (
      <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6" />
      </svg>
    ),
  },
  {
    name: 'Multi-Function Profiling System',
    tagline: 'AI Profiling & Analysis',
    description:
      'A multi-function appliance combining AI profiling, behavioral analysis, and operational analytics in a single ruggedized unit.',
    features: [
      'AI Profiling',
      'Behavioral Analysis',
      'Multi-Function',
      'Ruggedized',
    ],
    link: '/contact',
    color: 'emerald',
    icon: (
      <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 20.25h12m-7.5-3v3m3-3v3m-10.125-3h17.25c.621 0 1.125-.504 1.125-1.125V4.875c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125z" />
      </svg>
    ),
  },
];

const COLOR_MAP = {
  cyan: {
    iconBg: 'bg-cyan-500/10',
    iconText: 'text-cyan-400',
    badge: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20',
    bullet: 'text-cyan-400',
  },
  blue: {
    iconBg: 'bg-blue-500/10',
    iconText: 'text-blue-400',
    badge: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
    bullet: 'text-blue-400',
  },
  purple: {
    iconBg: 'bg-purple-500/10',
    iconText: 'text-purple-400',
    badge: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
    bullet: 'text-purple-400',
  },
  emerald: {
    iconBg: 'bg-emerald-500/10',
    iconText: 'text-emerald-400',
    badge: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    bullet: 'text-emerald-400',
  },
};

export default function Products() {
  return (
    <div className="relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-cyan-500/8 blur-3xl" />
        <div className="absolute top-1/2 -left-40 w-[400px] h-[400px] rounded-full bg-blue-500/8 blur-3xl" />
      </div>

      {/* ==================== HERO ==================== */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16 lg:pt-32 lg:pb-20">
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6">
            <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
            <span className="text-sm text-cyan-400 font-medium">Product Catalog</span>
          </div>

          <h1 className="text-4xl sm:text-5xl font-extrabold text-white leading-tight">
            Secure{' '}
            <span className="text-gradient">AI Solutions</span>
          </h1>

          <p className="mt-6 text-lg text-slate-400 leading-relaxed">
            A portfolio of AI-powered products designed for government and enterprise clients
            who require secure on-premise processing and operational reliability.
          </p>
        </div>
      </section>

      {/* ==================== PRODUCT CARDS ==================== */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {PRODUCTS.map((product) => {
            const colors = COLOR_MAP[product.color];
            return (
              <div
                key={product.name}
                className="group relative bg-navy-800/60 backdrop-blur border border-navy-700 rounded-2xl p-8 hover:border-cyan-500/20 transition-all duration-300 flex flex-col"
              >
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500/5 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />

                <div className="relative flex-1 flex flex-col">
                  {/* Header */}
                  <div className="flex items-start gap-5 mb-6">
                    <div
                      className={`shrink-0 w-16 h-16 rounded-2xl ${colors.iconBg} flex items-center justify-center ${colors.iconText}`}
                    >
                      {product.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">{product.name}</h3>
                      <span
                        className={`inline-block mt-1 text-xs font-semibold px-2.5 py-0.5 rounded-full border ${colors.badge}`}
                      >
                        {product.tagline}
                      </span>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-slate-400 leading-relaxed text-sm mb-6">
                    {product.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-2.5 mb-8 flex-1">
                    {product.features.map((feat) => (
                      <li key={feat} className="flex items-start gap-2.5 text-sm text-slate-300">
                        <svg
                          className={`w-4 h-4 mt-0.5 shrink-0 ${colors.bullet}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        {feat}
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <Link
                    to={product.link}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-400 hover:text-cyan-300 transition-colors"
                  >
                    {product.link === '/aibox' ? 'Learn More' : 'Contact for Details'}
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                    </svg>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p className="text-slate-400 mb-6">
            Need a custom solution or integration? Our engineering team is ready to help.
          </p>
          <Link
            to="/contact"
            className="inline-block px-10 py-3 btn-gradient text-white font-semibold rounded-lg"
          >
            Contact Our Team
          </Link>
        </div>
      </section>
    </div>
  );
}
